/**
 * Anchor-pinned text layout for Fabric Textbox / IText.
 * Reflows copy inside a fixed box (width/height + fontSize) without scaling the whole object.
 */

const MIN_FONT_SIZE = 6;
const MAX_FONT_SIZE = 800;
const DEFAULT_PADDING = 2;
const MIN_TEXTBOX_PX = 24;

/** Word wrap at spaces; false = break on word boundaries (not mid-word graphemes). */
const WORD_WRAP_STYLE = { splitByGrapheme: false };

/** @typedef {'top-left'|'top-center'|'top-right'|'center-left'|'center'|'center-right'|'bottom-left'|'bottom-center'|'bottom-right'} TextAnchor */

export function deriveAnchor(obj) {
  const ox = obj?.originX || 'left';
  const oy = obj?.originY || 'top';
  const y = oy === 'center' ? 'center' : oy === 'bottom' ? 'bottom' : 'top';
  const x = ox === 'center' ? 'center' : ox === 'right' ? 'right' : 'left';
  return `${y}-${x}`;
}

function parseAnchor(anchor) {
  const a = String(anchor || 'top-left');
  if (a === 'center') return { originY: 'center', originX: 'center' };
  const parts = a.split('-');
  return { originY: parts[0] || 'top', originX: parts[1] || 'left' };
}

export function anchorToOrigin(anchor) {
  const { originX, originY } = parseAnchor(anchor);
  return { originX, originY };
}

export function countIntendedLines(text) {
  const s = String(text ?? '');
  if (!s) return 1;
  return Math.max(1, s.split(/\r?\n/).length);
}

function getBoundingRectSafe(obj) {
  try {
    obj.setCoords?.();
    return obj.getBoundingRect(true);
  } catch {
    return { left: 0, top: 0, width: 200, height: 48 };
  }
}

/** Fixed point in canvas/parent space for the given anchor on the current bbox. */
export function snapshotAnchorPin(obj, anchor) {
  const a = anchor || deriveAnchor(obj);
  const { originX: ox, originY: oy } = parseAnchor(a);
  const br = getBoundingRectSafe(obj);
  let x = br.left;
  let y = br.top;
  if (ox === 'right') x = br.left + br.width;
  else if (ox === 'center') x = br.left + br.width / 2;
  if (oy === 'bottom') y = br.top + br.height;
  else if (oy === 'center') y = br.top + br.height / 2;
  return { anchor: a, x, y };
}

/** Reposition object so anchor point stays at pinned x,y after metrics changed. */
export function restoreAnchorPin(canvas, obj, pin) {
  if (!obj || !pin || typeof obj.setPositionByOrigin !== 'function') return;
  const { originX, originY } = anchorToOrigin(pin.anchor);
  const { fabric } = require('fabric');
  try {
    obj.set({ originX, originY, scaleX: 1, scaleY: 1 });
    obj.setPositionByOrigin(new fabric.Point(pin.x, pin.y), originX, originY);
    obj.setCoords?.();
    let g = obj.group;
    while (g) {
      g.setCoords?.();
      g = g.group;
    }
    canvas?.requestRenderAll?.() || canvas?.renderAll?.();
  } catch {
    /* ignore */
  }
}

function createMeasureTextbox(fabric, props) {
  return new fabric.Textbox(props.text || '', {
    fontSize: props.fontSize || 12,
    fontFamily: props.fontFamily || 'Arial',
    fontWeight: props.fontWeight || 'normal',
    fontStyle: props.fontStyle || 'normal',
    width: props.width || 200,
    lineHeight: props.lineHeight ?? 1.16,
    charSpacing: props.charSpacing ?? 0,
    splitByGrapheme: false,
    textAlign: props.textAlign || 'left',
  });
}

function measureLayout(fabric, props) {
  const tb = createMeasureTextbox(fabric, props);
  if (typeof tb.initDimensions === 'function') tb.initDimensions();
  const h = typeof tb.calcTextHeight === 'function' ? tb.calcTextHeight() : tb.height || 0;
  const lines = Array.isArray(tb._textLines) ? tb._textLines.length : countIntendedLines(props.text);
  return {
    width: tb.width || props.width,
    height: Math.max(1, h),
    lineCount: lines,
    fontSize: tb.fontSize,
  };
}

function computeFitBoxLayout(fabric, opts) {
  const {
    text,
    maxWidth,
    maxHeight,
    baseFontSize,
    minFontSize = MIN_FONT_SIZE,
    intendedLines = 1,
    fontFamily,
    fontWeight,
    fontStyle,
    lineHeight,
    charSpacing,
    textAlign,
  } = opts;

  const cap = Math.max(minFontSize, Math.min(MAX_FONT_SIZE, Math.floor(baseFontSize)));
  const minFs = Math.max(MIN_FONT_SIZE, minFontSize);
  const str = String(text);
  const singleLineIntent = intendedLines <= 1 && !/\r|\n/.test(str);

  function fits(fs, w) {
    const m = measureLayout(fabric, {
      text: str,
      width: w,
      fontSize: fs,
      fontFamily,
      fontWeight,
      fontStyle,
      lineHeight,
      charSpacing,
      textAlign,
    });
    const lineOk = singleLineIntent ? m.lineCount <= 1 : true;
    return lineOk && m.width <= maxWidth + 1.5 && m.height <= maxHeight + 1.5;
  }

  let localW = Math.max(24, maxWidth);
  if (singleLineIntent) {
    let probe = localW;
    for (let i = 0; i < 20 && !fits(cap, probe); i++) {
      probe = Math.min(localW * 1.25, probe + Math.max(12, localW * 0.06));
      localW = probe;
    }
  }

  let fs = cap;
  while (fs > minFs && !fits(fs, localW)) fs -= 1;
  while (fs < cap && fits(fs + 1, localW)) fs += 1;

  const final = measureLayout(fabric, {
    text: str,
    width: localW,
    fontSize: fs,
    fontFamily,
    fontWeight,
    fontStyle,
    lineHeight,
    charSpacing,
    textAlign,
  });

  return {
    fontSize: fs,
    width: localW,
    height: Math.min(maxHeight, Math.max(final.height, fs * 1.1)),
    lineCount: final.lineCount,
  };
}

/**
 * Snapshot design-time text box for fieldConfig (anchor + intended line structure preserved).
 */
export function snapshotTextFieldConfig(obj) {
  if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox' && obj.type !== 'text')) return undefined;
  try {
    obj.setCoords?.();
  } catch {
    /* ignore */
  }
  const sx = Math.abs(obj.scaleX || 1);
  const sy = Math.abs(obj.scaleY || 1);
  let br;
  try {
    br = obj.getBoundingRect(true);
  } catch {
    return { maxWidth: 240, maxHeight: 120, behavior: 'fitBox', baseFontSize: 12, anchor: 'top-left', intendedLines: 1 };
  }
  const lw = Number(obj.width);
  const lh = Number(obj.height);
  let boxW;
  if (obj.type === 'textbox') {
    boxW = Math.max(1, (Number.isFinite(lw) && lw > 0 ? lw : br.width / sx) * sx);
  } else {
    boxW = Math.max(1, Number.isFinite(lw) && lw > 0 ? lw * sx : br.width);
  }
  let boxH;
  if (obj.type === 'textbox' && Number.isFinite(lh) && lh > 0) {
    boxH = lh * sy;
  } else {
    boxH = Math.max(1, Number.isFinite(lh) && lh > 0 ? lh * sy : br.height);
  }
  const pad = DEFAULT_PADDING;
  const fs0 = Number(obj.fontSize);
  const baseFontSize = Math.round(Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, Number.isFinite(fs0) && fs0 > 0 ? fs0 : 12)));
  return {
    maxWidth: Math.max(MIN_TEXTBOX_PX, boxW),
    maxHeight: Math.max(MIN_TEXTBOX_PX, boxH),
    behavior: 'fitBox',
    baseFontSize,
    minFontSize: MIN_FONT_SIZE,
    anchor: deriveAnchor(obj),
    intendedLines: countIntendedLines(obj.text),
    padding: pad,
    textAlign: obj.textAlign || 'left',
    lineHeight: obj.lineHeight ?? 1.16,
    charSpacing: obj.charSpacing ?? 0,
  };
}

/**
 * Apply text inside box; preserves anchor. Does not scale object — only fontSize/width/height/text.
 */
export function applyTextLayout(canvas, obj, options = {}) {
  if (!obj || !canvas) return;
  const { fabric } = require('fabric');
  const text = String(options.text != null ? options.text : obj.text ?? '');
  const cfg = { ...(obj.fieldConfig || {}), ...(options.config || {}) };
  const behavior =
    cfg.behavior === 'none' ? 'none' : cfg.behavior === 'wrap' ? 'wrap' : 'fitBox';
  const padding = cfg.padding ?? DEFAULT_PADDING;
  const maxW = Math.max(1, Number(cfg.maxWidth) || 200);
  const maxH = Math.max(1, Number(cfg.maxHeight) || 48);
  const baseFs = Math.max(MIN_FONT_SIZE, Number(cfg.baseFontSize) || obj.fontSize || 12);
  const anchor = cfg.anchor || deriveAnchor(obj);
  const pin = snapshotAnchorPin(obj, anchor);
  const sx = Math.abs(obj.scaleX || 1);
  const localW = Math.max(1, (maxW - padding) / sx);
  const innerH = Math.max(1, maxH - padding);

  const style = {
    fontFamily: obj.fontFamily || 'Arial',
    fontWeight: obj.fontWeight || 'normal',
    fontStyle: obj.fontStyle || 'normal',
    lineHeight: cfg.lineHeight ?? obj.lineHeight ?? 1.16,
    charSpacing: cfg.charSpacing ?? obj.charSpacing ?? 0,
    textAlign: cfg.textAlign || obj.textAlign || 'left',
  };

  if (behavior === 'none') {
    obj.set({
      text,
      scaleX: 1,
      scaleY: 1,
      splitByGrapheme: true,
      ...style,
    });
    restoreAnchorPin(canvas, obj, pin);
    return;
  }

  if (behavior === 'wrap') {
    const preserveH =
      obj.type === 'textbox' && Number(obj.height) >= MIN_TEXTBOX_PX ? Number(obj.height) : null;
    obj.set({
      text,
      width: localW,
      fontSize: baseFs,
      scaleX: 1,
      scaleY: 1,
      ...WORD_WRAP_STYLE,
      ...(preserveH != null ? { height: preserveH } : {}),
      ...style,
    });
    if (obj.type === 'textbox' && typeof obj.initDimensions === 'function') {
      obj.initDimensions();
      if (preserveH == null) {
        const nh = typeof obj.calcTextHeight === 'function' ? obj.calcTextHeight() : baseFs * 2;
        obj.set('height', Math.max(MIN_TEXTBOX_PX, Math.min(maxH / sx, nh + padding)));
      }
    }
    restoreAnchorPin(canvas, obj, pin);
    return;
  }

  const intendedLines = cfg.intendedLines != null ? cfg.intendedLines : countIntendedLines(obj.text);
  const layout = computeFitBoxLayout(fabric, {
    text,
    maxWidth: localW,
    maxHeight: innerH,
    baseFontSize: baseFs,
    minFontSize: cfg.minFontSize ?? MIN_FONT_SIZE,
    intendedLines,
    ...style,
  });

  obj.set({
    text,
    width: layout.width,
    fontSize: layout.fontSize,
    scaleX: 1,
    scaleY: 1,
    splitByGrapheme: true,
    ...style,
  });
  if (obj.type === 'textbox') {
    obj.set('height', innerH);
    if (typeof obj.initDimensions === 'function') obj.initDimensions();
  }

  restoreAnchorPin(canvas, obj, pin);
}

/** Fabric Textbox clipPath misaligns and hides text — always clear it. */
export function syncTextboxClipPath(obj) {
  if (!obj || obj.type !== 'textbox') return;
  if (obj.clipPath) obj.clipPath = null;
}

const DEFAULT_TEXT_FILL = '#111827';

function isTextboxCornerHandle(corner) {
  return Boolean(corner && /^(tl|tr|bl|br)$/.test(String(corner)));
}

/** Corner drag: Fabric Textbox often only changes scaleX — apply uniform scale to width and height. */
function textboxScaleFactors(scaleX, scaleY, corner) {
  const sx = Math.abs(scaleX || 1);
  const sy = Math.abs(scaleY || 1);
  if (isTextboxCornerHandle(corner)) {
    const uni = Math.max(sx, sy);
    return { sx: uni, sy: uni };
  }
  return { sx, sy };
}

/** Remember size at start of a resize gesture (survives scale reset each frame). */
export function beginTextboxResizeSession(obj) {
  if (!obj || obj.type !== 'textbox' || obj.__tbResizeSession) return;
  const h = Number(obj.height);
  const sessionH =
    Number.isFinite(h) && h >= MIN_TEXTBOX_PX
      ? h
      : Math.max(
          MIN_TEXTBOX_PX,
          typeof obj.calcTextHeight === 'function' ? obj.calcTextHeight() : 40,
        );
  obj.__tbResizeSession = {
    width: Math.max(MIN_TEXTBOX_PX, Number(obj.width) || 100),
    height: sessionH,
  };
}

export function clearTextboxResizeSession(obj) {
  if (obj) delete obj.__tbResizeSession;
}

/** Recalculate wrapped lines for width; never collapse user-set box height. */
export function reflowTextboxWidthPreserveHeight(obj) {
  if (!obj || obj.type !== 'textbox') return;
  const savedH = Math.max(MIN_TEXTBOX_PX, Number(obj.height) || MIN_TEXTBOX_PX);
  if (typeof obj.initDimensions === 'function') obj.initDimensions();
  obj.set({ height: savedH, ...WORD_WRAP_STYLE });
  obj.setCoords?.();
}

/** Outer textbox size from current width/height (not shrunk snapshot). */
export function getTextboxBoxMetrics(obj) {
  const sx = Math.max(0.001, Math.abs(obj?.scaleX || 1));
  const sy = Math.max(0.001, Math.abs(obj?.scaleY || 1));
  const localW = Math.max(MIN_TEXTBOX_PX, Number(obj?.width) || 100);
  const localH = Math.max(MIN_TEXTBOX_PX, Number(obj?.height) || 40);
  return {
    sx,
    sy,
    localW,
    localH,
    boxW: localW * sx,
    boxH: localH * sy,
  };
}

/** Live resize while dragging handles — grows/shrinks box, not glyphs. */
export function resizeTextboxFromTransform(canvas, obj, options = {}) {
  if (!obj || obj.type !== 'textbox' || !canvas) return;
  const sx0 = Math.abs(obj.scaleX || 1);
  const sy0 = Math.abs(obj.scaleY || 1);
  if (Math.abs(sx0 - 1) < 0.002 && Math.abs(sy0 - 1) < 0.002) return;

  beginTextboxResizeSession(obj);
  const session = obj.__tbResizeSession;
  const corner = options.corner || obj.__corner;
  let { sx, sy } = textboxScaleFactors(sx0, sy0, corner);
  if (!isTextboxCornerHandle(corner) && Math.abs(sy - 1) < 0.02 && sx > 1.02) {
    sy = sx;
  }

  const newW = Math.max(MIN_TEXTBOX_PX, session.width * sx);
  const newH = Math.max(MIN_TEXTBOX_PX, session.height * sy);
  const prevFill = obj.fill || DEFAULT_TEXT_FILL;
  const live = options.live === true;
  const pin = snapshotAnchorPin(obj, deriveAnchor(obj));

  obj.set({
    width: newW,
    height: newH,
    scaleX: 1,
    scaleY: 1,
    fill: prevFill,
    lockScalingY: false,
    lockScalingX: false,
    lockUniScaling: false,
    ...WORD_WRAP_STYLE,
  });

  if (!live) reflowTextboxWidthPreserveHeight(obj);
  restoreAnchorPin(canvas, obj, pin);
  obj.setCoords?.();

  canvas.requestRenderAll?.() || canvas.renderAll?.();
}

/** End of resize — keep final box size and reflow copy inside it. */
export function commitTextboxResize(canvas, obj) {
  if (!obj || obj.type !== 'textbox' || !canvas) return;
  clearTextboxResizeSession(obj);
  syncTextboxClipPath(obj);
  const pin = snapshotAnchorPin(obj, deriveAnchor(obj));
  const { localW, localH } = getTextboxBoxMetrics(obj);
  const prevFill = obj.fill || DEFAULT_TEXT_FILL;

  obj.set({
    width: localW,
    height: localH,
    scaleX: 1,
    scaleY: 1,
    fill: prevFill,
    lockScalingY: false,
    lockScalingX: false,
    lockUniScaling: false,
    ...WORD_WRAP_STYLE,
  });
  reflowTextboxWidthPreserveHeight(obj);
  restoreAnchorPin(canvas, obj, pin);
  try {
    obj.fieldConfig = { ...(obj.fieldConfig || {}), ...snapshotTextFieldConfig(obj) };
  } catch {
    /* ignore */
  }
  ensureTextObjectVisible(obj);
  canvas.requestRenderAll?.() || canvas.renderAll?.();
}

/** Wrap copy inside the current box; shrink font only if lines overflow height. */
export function reflowTextboxCopy(canvas, obj, options = {}) {
  if (!obj || obj.type !== 'textbox' || !canvas) return;
  syncTextboxClipPath(obj);
  const { boxW, boxH, localW, localH } = getTextboxBoxMetrics(obj);
  const cfg = { ...(obj.fieldConfig || {}), ...(options.config || {}) };
  const text = String(options.text != null ? options.text : obj.text ?? '');
  const pin = snapshotAnchorPin(obj, cfg.anchor || deriveAnchor(obj));
  const prevFill = obj.fill || DEFAULT_TEXT_FILL;
  const baseFs = Math.max(MIN_FONT_SIZE, Number(cfg.baseFontSize) || obj.fontSize || 12);

  applyTextLayout(canvas, obj, {
    text,
    config: {
      ...cfg,
      maxWidth: boxW,
      maxHeight: boxH,
      behavior: 'wrap',
      baseFontSize: baseFs,
      intendedLines: cfg.intendedLines != null ? cfg.intendedLines : countIntendedLines(text),
    },
  });

  obj.set({
    width: localW,
    height: localH,
    scaleX: 1,
    scaleY: 1,
    fill: prevFill,
    ...WORD_WRAP_STYLE,
  });
  reflowTextboxWidthPreserveHeight(obj);

  try {
    obj.setCoords?.();
    const pad = cfg.padding ?? DEFAULT_PADDING;
    const innerH = Math.max(16, localH - pad);
    const nh = typeof obj.calcTextHeight === 'function' ? obj.calcTextHeight() : 0;
    if (nh > innerH + 1) {
      applyTextLayout(canvas, obj, {
        text,
        config: { ...cfg, maxWidth: boxW, maxHeight: boxH, behavior: 'fitBox', baseFontSize: baseFs },
      });
      obj.set({
        width: localW,
        height: localH,
        scaleX: 1,
        scaleY: 1,
        fill: prevFill,
        ...WORD_WRAP_STYLE,
      });
      reflowTextboxWidthPreserveHeight(obj);
    }
  } catch {
    /* keep wrap */
  }

  restoreAnchorPin(canvas, obj, pin);
  try {
    obj.fieldConfig = { ...(obj.fieldConfig || {}), ...snapshotTextFieldConfig(obj) };
  } catch {
    /* ignore */
  }
  ensureTextObjectVisible(obj);
  canvas.requestRenderAll?.() || canvas.renderAll?.();
}

/** After resize/move: keep box size the user set. */
export function finishTextboxTransform(canvas, obj) {
  commitTextboxResize(canvas, obj);
}

/** Keep glyphs visible (fill, size, cache) without re-running full layout. */
export function ensureTextObjectVisible(obj) {
  if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox' && obj.type !== 'text')) return;
  syncTextboxClipPath(obj);
  const fillStr = String(obj.fill || '').trim().toLowerCase();
  if (!fillStr || fillStr === '#ffffff' || fillStr === '#fff' || fillStr === 'white' || fillStr === 'transparent') {
    obj.set('fill', DEFAULT_TEXT_FILL);
  }
  const fs = Number(obj.fontSize);
  if (!Number.isFinite(fs) || fs < MIN_FONT_SIZE) {
    obj.set('fontSize', Math.max(MIN_FONT_SIZE, 12));
  }
  try {
    obj.set({ objectCaching: false, opacity: obj.opacity ?? 1 });
    if (obj.type === 'textbox') {
      reflowTextboxWidthPreserveHeight(obj);
    }
    obj.setCoords?.();
  } catch {
    /* ignore */
  }
}

/**
 * Keep copy inside the text area: wrap at design size, shrink if needed, fixed box + clip.
 * Preserves explicit line breaks (\\n) from the design.
 */
export function applyTextContainInBox(canvas, obj, options = {}) {
  if (!obj || !canvas) return;
  if (obj.type === 'textbox') {
    reflowTextboxCopy(canvas, obj, options);
    return;
  }
  syncTextboxClipPath(obj);
  const merged = { ...(obj.fieldConfig || {}), ...(options.config || {}) };
  const text = String(options.text != null ? options.text : obj.text ?? '');
  const sx = Math.max(0.001, Math.abs(obj.scaleX || 1));
  const sy = Math.max(0.001, Math.abs(obj.scaleY || 1));
  const boxW = Math.max(MIN_TEXTBOX_PX, Number(merged.maxWidth) || (obj.width || 100) * sx);
  const boxH = Math.max(MIN_TEXTBOX_PX, Number(merged.maxHeight) || (obj.height || 40) * sy);
  merged.maxWidth = boxW;
  merged.maxHeight = boxH;
  merged.intendedLines = merged.intendedLines != null ? merged.intendedLines : countIntendedLines(text);
  merged.baseFontSize = Math.max(MIN_FONT_SIZE, Number(merged.baseFontSize) || obj.fontSize || 12);
  const anchor = merged.anchor || deriveAnchor(obj);
  const pin = snapshotAnchorPin(obj, anchor);
  const prevFill = obj.fill || DEFAULT_TEXT_FILL;

  applyTextLayout(canvas, obj, {
    text,
    config: { ...merged, behavior: 'wrap' },
  });

  try {
    obj.setCoords?.();
    const br = obj.getBoundingRect(true);
    if (br.height > boxH + 1) {
      applyTextLayout(canvas, obj, {
        text,
        config: { ...merged, behavior: 'fitBox' },
      });
    }
  } catch {
    /* ignore */
  }
  ensureTextObjectVisible(obj);
  canvas.requestRenderAll?.() || canvas.renderAll?.();
}

/** Convert corner scale drag into box resize + reflow (no glyph scaling). */
export function absorbTextScaleIntoBox(canvas, obj, options = {}) {
  if (!obj || !canvas) return;
  if (obj.type !== 'textbox' && obj.type !== 'i-text' && obj.type !== 'text') return;

  const sx = obj.scaleX || 1;
  const sy = obj.scaleY || 1;
  if (Math.abs(sx - 1) < 0.002 && Math.abs(sy - 1) < 0.002) return;

  syncTextboxClipPath(obj);

  const cfg = { ...(obj.fieldConfig || {}), ...snapshotTextFieldConfig(obj) };
  const live = options.live === true;

  if (obj.type === 'textbox') {
    if (live) resizeTextboxFromTransform(canvas, obj, { live: true, corner: obj.__corner });
    else commitTextboxResize(canvas, obj);
    return;
  }

  obj.setCoords?.();
  const br = obj.getBoundingRect(true);
  cfg.maxWidth = Math.max(24, br.width);
  cfg.maxHeight = Math.max(16, br.height);
  obj.set({ scaleX: 1, scaleY: 1 });
  applyTextLayout(canvas, obj, {
    text: obj.text,
    config: { ...cfg, behavior: live ? 'wrap' : cfg.behavior || 'fitBox' },
  });
}

/**
 * Export/PDF: keep design fontSize and position — only normalize scale into width/height.
 */
export function prepareCanvasTextForExport(canvas) {
  if (!canvas) return;
  const walk = (obj) => {
    if (obj.type !== 'i-text' && obj.type !== 'textbox' && obj.type !== 'text') return;
    syncTextboxClipPath(obj);
    try {
      obj.set({ splitByGrapheme: true });
    } catch {
      /* ignore */
    }
    const sx = Math.abs(obj.scaleX || 1);
    const sy = Math.abs(obj.scaleY || 1);
    if (Math.abs(sx - 1) < 0.002 && Math.abs(sy - 1) < 0.002) {
      obj.setCoords?.();
      return;
    }
    const fs = obj.fontSize || 12;
    const anchor = (obj.fieldConfig && obj.fieldConfig.anchor) || deriveAnchor(obj);
    const pin = snapshotAnchorPin(obj, anchor);
    if (obj.type === 'textbox') {
      const w = Math.max(1, (obj.width || 100) * sx);
      const h = Math.max(1, (obj.height || 40) * sy);
      obj.set({ width: w, height: h, scaleX: 1, scaleY: 1, fontSize: fs });
      if (typeof obj.initDimensions === 'function') obj.initDimensions();
    } else {
      obj.set({ scaleX: 1, scaleY: 1, fontSize: fs });
    }
    restoreAnchorPin(canvas, obj, pin);
    obj.setCoords?.();
  };
  const deep = (obj) => {
    walk(obj);
    if (obj.type === 'group' && typeof obj.getObjects === 'function') {
      obj.getObjects().forEach(deep);
    }
  };
  canvas.getObjects().forEach(deep);
  canvas.renderAll?.();
}

/** Dynamic placeholder: design size first; shrink only if copy overflows the saved box. */
export function applyDynamicFieldText(canvas, obj, text, cfg = {}) {
  if (!obj || !canvas) return;
  const merged = { ...(obj.fieldConfig || {}), ...cfg };
  const str = String(text);
  if (obj.type === 'textbox') {
    const sx = Math.max(0.001, Math.abs(obj.scaleX || 1));
    const sy = Math.max(0.001, Math.abs(obj.scaleY || 1));
    const designW = Number(merged.maxWidth);
    const designH = Number(merged.maxHeight);
    if (designW >= MIN_TEXTBOX_PX && designH >= MIN_TEXTBOX_PX) {
      obj.set({ width: designW / sx, height: designH / sy, scaleX: 1, scaleY: 1 });
    }
    reflowTextboxCopy(canvas, obj, { text: str, config: merged });
    return;
  }
  const baseFs = Math.max(MIN_FONT_SIZE, Number(merged.baseFontSize) || obj.fontSize || 12);
  applyTextLayout(canvas, obj, {
    text: str,
    config: {
      ...merged,
      behavior: 'wrap',
      baseFontSize: baseFs,
    },
  });
  const maxH = Number(merged.maxHeight);
  const maxW = Number(merged.maxWidth);
  if (!maxH && !maxW) return;
  try {
    obj.setCoords?.();
    const br = obj.getBoundingRect(true);
    const overH = maxH > 0 && br.height > maxH + 2;
    const overW = maxW > 0 && br.width > maxW + 2;
    if (overH || overW) {
      applyTextLayout(canvas, obj, {
        text: str,
        config: { ...merged, behavior: 'fitBox', baseFontSize: baseFs },
      });
    }
  } catch {
    /* keep wrap result */
  }
}

export function fitTextObjectsForExport(canvas, pageW, pageH, options = {}) {
  if (options.preserveDesignSize !== false) {
    prepareCanvasTextForExport(canvas);
  }
  if (!options.reflowDynamicFields) return;

  const pad = 6;
  const maxOuterW = pageW - pad * 2;
  const maxOuterH = pageH - pad * 2;

  const walk = (obj) => {
    if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') {
      const fk = obj.fieldKey || (typeof obj.get === 'function' ? obj.get('fieldKey') : null);
      if (!fk) return;
      const cfg = { ...(obj.fieldConfig || {}) };
      if (!cfg.baseFontSize) cfg.baseFontSize = obj.fontSize;
      if (!cfg.maxWidth || !cfg.maxHeight) {
        const snap = snapshotTextFieldConfig(obj);
        if (snap) {
          if (!cfg.maxWidth) cfg.maxWidth = snap.maxWidth;
          if (!cfg.maxHeight) cfg.maxHeight = snap.maxHeight;
          if (!cfg.anchor) cfg.anchor = snap.anchor;
        }
      }
      const sx = Math.abs(obj.scaleX || 1);
      const boxW = Math.min(maxOuterW, (cfg.maxWidth || 200) * sx);
      const boxH = Math.min(maxOuterH, (cfg.maxHeight || 48) * (Math.abs(obj.scaleY || 1)));
      if ((cfg.behavior || 'fitBox') === 'fitBox') {
        applyTextLayout(canvas, obj, {
          text: obj.text || '',
          config: {
            ...cfg,
            maxWidth: boxW,
            maxHeight: boxH,
            behavior: 'fitBox',
          },
        });
      }
    }
    if (obj.type === 'group' && typeof obj.getObjects === 'function') {
      obj.getObjects().forEach(walk);
    }
  };

  canvas.getObjects().forEach(walk);
  canvas.renderAll?.();
}
